<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';

import type { Deployment } from '@clovyr/pollen';
import { AuthState } from '@clovyr/pollen/pollen/types';
import { UserAPI } from '@clovyr/pollen/user';

import ClovyrLogoSmall from '@/components/icons/ClovyrLogoSmall.vue';
import LockIcon from '@/components/icons/LockIcon.vue';
import { useLibraryFlashStore } from '@/stores/library_flash';
import { usePollenStore } from '@/stores/pollen_store';

import { debugPasskey } from './local_sso.ts';
import LocalSsoDialog from './LocalSsoDialog.vue';

const route = useRoute();
const router = useRouter();
const pollenStore = usePollenStore();
const libraryFlash = useLibraryFlashStore();
const fragment = window.location.hash.slice(1);
const invitation = new URLSearchParams(fragment);
const combinedInvite = invitation.has('token');
const token = combinedInvite ? (invitation.get('token') ?? '') : fragment;
const shareSecret = invitation.get('key');
const message = ref('');
const redeeming = ref(false);
const needsPasskey = ref(false);
const registeringPasskey = ref(false);
const accepted = ref(false);
let destination: RouteLocationRaw = { name: 'Library' };

function appDetailDestination(deployment: Deployment): RouteLocationRaw {
  const params = pollenStore.getLibraryRouteParams(deployment);
  return {
    name: 'libraryItemPrefix' in params ? 'LibraryAppDetailQualified' : 'LibraryAppDetail',
    params,
  };
}

async function acceptInvite() {
  if (combinedInvite) {
    if (!shareSecret) throw new Error('Missing library key');
    const libraryItem = await new UserAPI().getLocalSsoInviteLibrary(token);
    await pollenStore.pollen.acceptAICloudInvite(token, shareSecret);
    const deployment = pollenStore.getDeploymentByLibraryItemID(libraryItem.id);
    if (!deployment) throw new Error('Invited application is missing from the library');
    destination = appDetailDestination(deployment);
    libraryFlash.show(
      libraryItem.id,
      'AI Cloud has been added to your library. Ask an owner or editor to refresh passkeys before you sign in.',
    );
  } else {
    const invite = await new UserAPI().redeemLocalSsoInvite(token);
    const deployment = pollenStore.getDeploymentByFQDN(invite.deploymentFqdn);
    if (deployment) destination = appDetailDestination(deployment);
  }
  accepted.value = true;
  window.history.replaceState(null, '', window.location.pathname);
  await router.replace(destination);
}

async function redeem() {
  if (accepted.value) {
    await router.replace(destination);
    return;
  }
  if (!token) {
    message.value = 'This invite link is invalid.';
    return;
  }
  if (
    !pollenStore.isUserAuthenticated ||
    (combinedInvite && pollenStore.authState !== AuthState.LoggedIn)
  ) {
    await router.push({ path: '/login', query: { redirect: route.fullPath } });
    return;
  }
  redeeming.value = true;
  message.value = '';
  try {
    const passkeys = await pollenStore.pollen.listPasskeys();
    debugPasskey(
      'account passkeys',
      passkeys.map((passkey) => ({
        id: passkey.id,
        rpId: passkey.rpId,
        transports: passkey.transports,
        backupEligible: passkey.backupEligible,
        backupState: passkey.backupState,
        vaultUnlock: passkey.vaultUnlock,
      })),
    );
    if (passkeys.length === 0) {
      needsPasskey.value = true;
      message.value = 'Add a passkey to accept this invite.';
      return;
    }
    await acceptInvite();
  } catch {
    message.value = accepted.value
      ? 'Invite accepted. Unable to open the library. Please try again.'
      : 'This invite has expired, was revoked, or cannot be redeemed with this account.';
  } finally {
    redeeming.value = false;
  }
}

async function addPasskey() {
  registeringPasskey.value = true;
  message.value = '';
  try {
    const passkey = await pollenStore.pollen.registerPasskey('Clovyr passkey');
    debugPasskey('passkey registered', {
      origin: window.location.origin,
      id: passkey.id,
      rpId: passkey.rpId,
      transports: passkey.transports,
      backupEligible: passkey.backupEligible,
      backupState: passkey.backupState,
      vaultUnlock: passkey.vaultUnlock,
    });
    needsPasskey.value = false;
    await redeem();
  } catch {
    message.value = 'Unable to add a passkey. Please try again.';
  } finally {
    registeringPasskey.value = false;
  }
}
</script>

<template>
  <LocalSsoDialog
    title="Accept private app access"
    description="Use your Clovyr account to securely join this private application."
  >
    <div class="local-sso-invite__divider"></div>

    <p v-if="message" class="local-sso-invite__status" role="status">{{ message }}</p>

    <button
      v-if="!needsPasskey"
      class="local-sso-invite__continue"
      type="button"
      :disabled="redeeming"
      :aria-busy="redeeming"
      @click="redeem"
    >
      <ClovyrLogoSmall class="local-sso__continue-mark" aria-hidden="true" />
      <span>{{ accepted ? 'Continue' : redeeming ? 'Accepting...' : 'Accept invite' }}</span>
      <i class="las la-arrow-right local-sso-invite__continue-arrow"></i>
    </button>
    <button
      v-else
      class="local-sso-invite__continue"
      type="button"
      :disabled="registeringPasskey"
      :aria-busy="registeringPasskey"
      @click="addPasskey"
    >
      <ClovyrLogoSmall class="local-sso__continue-mark" aria-hidden="true" />
      <span>{{ registeringPasskey ? 'Adding passkey...' : 'Add passkey and accept' }}</span>
      <i class="las la-arrow-right local-sso-invite__continue-arrow"></i>
    </button>

    <p class="local-sso-invite__redirect-note">
      Your passkey verification metadata is shared securely with the app owner.
    </p>

    <div class="local-sso-invite__trust">
      <LockIcon />
      <div>
        <h2>Private Authentication</h2>
        <p>
          Your password is never shared with the application. Clovyr only verifies the passkey
          metadata needed to grant you access.
        </p>
      </div>
    </div>
  </LocalSsoDialog>
</template>

<style lang="scss" scoped>
.local-sso-invite__host,
.local-sso-invite__trust,
.local-sso-invite__status {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.local-sso-invite__host {
  padding: 20px;
  margin-top: 32px;
  color: #83769c;
  background: #f8f7fa;
  border: 1px solid #e5e3e8;
  border-radius: 20px;
  font-size: clamp(18px, 3vw, 20px);
  letter-spacing: -0.035em;
  line-height: 1.25;
}

.local-sso-invite__host svg,
.local-sso-invite__trust > svg {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  stroke: currentcolor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.75;
}

.local-sso-invite__host strong {
  color: #251d3a;
  font-weight: 700;
}

.local-sso-invite__divider {
  height: 1px;
  margin: 32px 0;
  background: #dedde3;
}

.local-sso-invite__continue {
  display: grid;
  grid-template-columns: 24px 1fr 24px;
  gap: 12px;
  align-items: center;
  width: 100%;
  min-height: 64px;
  padding: 12px 20px;
  color: #fff;
  background: #5cbd85;
  border: 0;
  border-radius: 20px;
  box-shadow: 0 0.8rem 1.5rem rgb(92 189 133 / 20%);
  cursor: pointer;
  font: inherit;
  font-size: clamp(18px, 3vw, 20px);
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1.25;
  transition:
    background-color 150ms ease,
    transform 150ms ease,
    box-shadow 150ms ease;
}

.local-sso-invite__continue:focus-visible {
  outline: 3px solid #251d3a;
  outline-offset: 3px;
}

.local-sso-invite__continue:disabled {
  cursor: wait;
  opacity: 0.7;
}

.local-sso-invite__continue:hover:not(:disabled) {
  background: #43ae70;
  box-shadow: 0 1rem 1.75rem rgb(92 189 133 / 28%);
  transform: translateY(-1px);
}

.local-sso-invite__continue svg {
  width: 24px;
  height: 24px;
}

.local-sso-invite__continue-mark {
  justify-self: start;
}

.local-sso-invite__continue-arrow {
  justify-self: end;
  stroke: currentcolor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
  font-size: 1.5em;
}

.local-sso-invite__status {
  padding: 16px 20px;
  margin: 0 0 24px;
  color: #5c5470;
  background: #f8f7fa;
  border: 1px solid #e5e3e8;
  border-radius: 16px;
  font-size: clamp(14px, 2.6vw, 16px);
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.35;
}

.local-sso-invite__redirect-note {
  margin: 16px 0 0;
  color: #aaa3b7;
  font-size: clamp(14px, 2.6vw, 16px);
  letter-spacing: -0.025em;
  line-height: 1.5;
  text-align: center;
}

.local-sso-invite__trust {
  padding: 20px;
  margin-top: 32px;
  color: #83769c;
  background: #f8f7fa;
  border: 1px solid #e5e3e8;
  border-radius: 20px;
}

.local-sso-invite__trust > svg {
  color: #5cbd85;
}

.local-sso-invite__trust h2,
.local-sso-invite__trust p {
  margin: 0;
}

.local-sso-invite__trust h2 {
  color: #251d3a;
  font-size: clamp(18px, 2.5vw, 20px);
  font-weight: 700;
  letter-spacing: -0.035em;
  line-height: 1.3;
}

.local-sso-invite__trust p {
  margin-top: 6px;
  font-size: clamp(14px, 2.5vw, 16px);
  letter-spacing: -0.025em;
  line-height: 1.5;
}

@media (width <= 40rem) {
  .local-sso-invite__host {
    padding: 20px;
    margin-top: 28px;
    border-radius: 20px;
  }

  .local-sso-invite__divider {
    margin: 28px 0 24px;
  }

  .local-sso-invite__continue {
    min-height: 56px;
    border-radius: 22px;
  }

  .local-sso-invite__trust,
  .local-sso-invite__status {
    gap: 14px;
    padding: 20px;
    border-radius: 20px;
  }
}
</style>
